import type {
  LocationQueryRaw,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router
} from 'vue-router';
import type { RouteKey, RoutePath } from '@elegant-router/types';
import { useAuthStore } from '@/store/modules/auth';
import { useRouteStore } from '@/store/modules/route';
import { localStg } from '@/utils/storage';
import { useSearchCacheStore } from "@/store/modules/searchCache";


/**
 * create route guard
 *
 * @param router router instance
 */
export function createRouteGuard(router: Router) {
  const { handleCache } = useSearchCacheStore();
  router.beforeEach(async (to, from, next) => {
    /**
     * 根据 to from 判断是否需要使用缓存查询
     */
    handleCache(to.name, from.name)
    const location = await initRoute(to);

    if (location) {
      next(location);
      return;
    }

    const authStore = useAuthStore();

    const rootRoute: RouteKey = 'root';
    const loginRoute: RouteKey = 'login';
    const noAuthorizationRoute: RouteKey = '403';

    const isLogin = Boolean(localStg.get('token'));
    const needLogin = !to.meta.constant;

    const routeRoles = to.meta.menuId || [];

    const hasRole = authStore.menuIdList.value.some(role => routeRoles.includes(role));

    const hasAuth = authStore.isStaticSuper || hasRole;

    const routeSwitches: CommonType.StrategicPattern[] = [
      // 如果登录时为登录路由，则切换到根页面
      {
        condition: isLogin && to.name === loginRoute,
        callback: () => {
          next({ name: rootRoute });
        }
      },
      // 如果是固定路由，则允许直接访问
      {
        condition: !needLogin,
        callback: () => {
          handleRouteSwitch(to, from, next);
        }
      },
      // 如果路由需要登录，但用户没有登录，则切换到登录页面
      {
        condition: !isLogin && needLogin,
        callback: () => {
          next({ name: loginRoute });
        }
      },
      // 如果用户已登录并拥有授权，则允许其访问
      {
        condition: isLogin && needLogin && hasAuth,
        callback: () => {
          handleRouteSwitch(to, from, next);
        }
      },
      // 如果用户已登录但没有权限，则转到403页面。
      {
        condition: isLogin && needLogin && !hasAuth,
        callback: () => {
          next({ name: noAuthorizationRoute });
        }
      }
    ];

    routeSwitches.some(({ condition, callback }) => {
      if (condition) {
        callback();
      }

      return condition;
    });
  });
}

/**
 * 初始化路径
 *
 * @param to to route
 */
async function initRoute(to: RouteLocationNormalized): Promise<RouteLocationRaw | null> {
  const routeStore = useRouteStore();

  const notFoundRoute: RouteKey = 'not-found';
  const isNotFoundRoute = to.name === notFoundRoute;

  // 如果没有初始化常量路由，则初始化常量路由
  if (!routeStore.isInitConstantRoute) {
    await routeStore.initConstantRoute();

    // 因为常量路由未初始化，因此该路由被“未找到”路由捕获。
    // 常量路由初始化后，重定向到原路由
    if (isNotFoundRoute) {
      const path = to.fullPath;

      const location: RouteLocationRaw = {
        path,
        replace: true,
        query: to.query,
        hash: to.hash
      };

      return location;
    }
  }

  // 如果路由是常量路由，但不是“未找到”路由，则允许其访问。
  if (to.meta.constant && !isNotFoundRoute) {
    return null;
  }

  // 初始化认证路由
  // 它不是“未找到”路由，那么它被允许访问
  if (routeStore.isInitAuthRoute && !isNotFoundRoute) {
    return null;
  }
  // 被“not-found”路由捕获，然后检查路由是否存在
  if (routeStore.isInitAuthRoute && isNotFoundRoute) {
    const exist = await routeStore.getIsAuthRouteExist(to.path as RoutePath);
    const noPermissionRoute: RouteKey = '403';

    if (exist) {
      const location: RouteLocationRaw = {
        name: noPermissionRoute
      };

      return location;
    }

    return null;
  }

  // 如果没有初始化认证路由，则初始化认证路由
  const isLogin = Boolean(localStg.get('token'));
  // 初始化认证路由需要用户登录，否则重定向到登录页面
  if (!isLogin) {
    const loginRoute: RouteKey = 'login';

    const location: RouteLocationRaw = {
      name: loginRoute,

    };

    return location;
  }

  // 初始化授权路由
  await routeStore.initAuthRoute();

  // 因为授权路由尚未初始化，因此该路由被“未找到”路由捕获。
  // 在授权路由初始化完成后，重定向到原始路由。
  if (isNotFoundRoute) {
    const rootRoute: RouteKey = 'root';
    const path = to.redirectedFrom?.name === rootRoute ? '/' : to.fullPath;

    const location: RouteLocationRaw = {
      path,
      replace: true,
      query: to.query,
      hash: to.hash
    };

    return location;
  }

  return null;
}

function handleRouteSwitch(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  // route with href
  if (to.meta.href) {
    window.open(to.meta.href, '_blank');

    next({ path: from.fullPath, replace: true, query: from.query, hash: to.hash });

    return;
  }

  next();
}
